import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UserService } from '../../../layout/service/user.service';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ProgressBarModule } from 'primeng/progressbar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { Router } from '@angular/router';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';

pdfMake.vfs = pdfFonts['vfs'] as unknown as { [file: string]: string };

@Component({
    selector: 'app-participant-history',
    standalone: true,
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        DialogModule,
        ToastModule,
        ProgressBarModule,
        SelectModule,
        FormsModule,
        TooltipModule
    ],
    providers: [MessageService],
    template: `
        <p-toast />
        <div class="card">
            <p-progressbar *ngIf="fetchingData" mode="indeterminate" [style]="{ height: '6px' }" />
            
            <div class="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-6">
                <div>
                    <h3 class="text-primary m-0">Riwayat Checklist OSCE Peserta</h3>
                    <p class="text-sm text-gray-500 mt-1">Pilih peserta terlebih dahulu untuk melihat dan mengunduh riwayat checklist OSCE mereka.</p>
                </div>
            </div>

            <!-- Pemilihan Peserta -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div class="flex flex-col gap-2">
                    <label class="font-bold text-gray-700">Pilih Peserta</label>
                    <p-select 
                        [options]="participants" 
                        [(ngModel)]="selectedParticipant" 
                        optionLabel="FULLNAME"
                        [filter]="true"
                        filterBy="FULLNAME,USERNAME"
                        [showClear]="true"
                        placeholder="Cari nama atau username peserta..."
                        (onChange)="onParticipantChange()"
                        class="w-full">
                        <ng-template pTemplate="selectedItem" let-selectedOption>
                            <div class="flex items-center gap-2" *ngIf="selectedOption">
                                <div>{{ selectedOption.FULLNAME }} ({{ selectedOption.USERNAME }})</div>
                            </div>
                        </ng-template>
                        <ng-template let-user pTemplate="item">
                            <div class="flex flex-col">
                                <span class="font-semibold">{{ user.FULLNAME }}</span>
                                <span class="text-xs text-gray-500">{{ user.USERNAME }}</span>
                            </div>
                        </ng-template>
                    </p-select>
                </div>
            </div>

            <!-- Tab Segment Pilihan: Sebagai Peserta vs Sebagai Penguji -->
            <div *ngIf="selectedParticipant" class="flex border-b border-gray-200 mb-6">
                <button 
                    (click)="setTab('peserta')"
                    class="py-3 px-6 font-semibold text-sm focus:outline-none transition-all duration-200 border-b-2"
                    [ngClass]="activeTab === 'peserta' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'">
                    Sebagai Peserta (Diuji)
                </button>
                <button 
                    (click)="setTab('tutor')"
                    class="py-3 px-6 font-semibold text-sm focus:outline-none transition-all duration-200 border-b-2"
                    [ngClass]="activeTab === 'tutor' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'">
                    Sebagai Penguji / Tutor
                </button>
            </div>

            <!-- Empty State jika belum pilih peserta -->
            <div *ngIf="!selectedParticipant" class="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                <i class="pi pi-user text-6xl text-gray-400 mb-4"></i>
                <h4 class="font-bold text-gray-700 text-lg">Belum Ada Peserta Terpilih</h4>
                <p class="text-sm text-gray-500 text-center max-w-sm mt-1">Silakan cari dan pilih salah satu peserta dari dropdown di atas untuk menampilkan riwayat pengerjaan checklist.</p>
            </div>

            <!-- Tabel Riwayat jika peserta terpilih -->
            <div *ngIf="selectedParticipant">
                <p-table 
                    #dt 
                    [value]="results" 
                    stripedRows 
                    [tableStyle]="{ 'min-width': '50rem' }" 
                    [rowHover]="true" 
                    [rows]="10"
                    [showCurrentPageReport]="true" 
                    [rowsPerPageOptions]="[10, 25, 50]" 
                    [loading]="loading"
                    [paginator]="true" 
                    currentPageReportTemplate="Menampilkan {first} ke {last} dari {totalRecords} data"
                    [filterDelay]="0" 
                    [globalFilterFields]="['KATEGORI','JUDUL','UNIVERSITAS','CREATED_BY','MAHASISWA']">

                    <ng-template pTemplate="caption">
                        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <h4 class="font-semibold text-gray-800 m-0">
                                {{ activeTab === 'peserta' ? 'Daftar Riwayat Ujian (Diuji):' : 'Daftar Evaluasi yang Diisi (Menguji):' }} 
                                {{ selectedParticipant.FULLNAME }}
                            </h4>
                            <input 
                                pInputText 
                                type="text" 
                                (input)="onInput($event, dt)" 
                                placeholder="Cari kata kunci..." 
                                class="w-full md:w-80" />
                        </div>
                    </ng-template>

                    <ng-template pTemplate="header">
                        <tr>
                            <th class="w-16 text-center">No</th>
                            <th pSortableColumn="CREATED_AT">Tanggal <p-sortIcon field="CREATED_AT"></p-sortIcon></th>
                            <th pSortableColumn="KATEGORI">Kategori <p-sortIcon field="KATEGORI"></p-sortIcon></th>
                            <th pSortableColumn="JUDUL">Judul <p-sortIcon field="JUDUL"></p-sortIcon></th>
                            <th *ngIf="activeTab === 'peserta'" pSortableColumn="CREATED_BY">Tutor / Penguji <p-sortIcon field="CREATED_BY"></p-sortIcon></th>
                            <th *ngIf="activeTab === 'tutor'" pSortableColumn="MAHASISWA">Nama Mahasiswa <p-sortIcon field="MAHASISWA"></p-sortIcon></th>
                            <th pSortableColumn="BOBOT" class="w-24 text-center">Bobot <p-sortIcon field="BOBOT"></p-sortIcon></th>
                            <th pSortableColumn="SKOR" class="w-24 text-center">Skor (%) <p-sortIcon field="SKOR"></p-sortIcon></th>
                            <th pSortableColumn="UNIVERSITAS">Universitas <p-sortIcon field="UNIVERSITAS"></p-sortIcon></th>
                            <th class="w-40 text-center">Aksi</th>
                        </tr>
                    </ng-template>

                    <ng-template pTemplate="body" let-result let-i="rowIndex">
                        <tr class="cursor-pointer" (click)="viewDetail(result)">
                            <td class="text-center">{{ i + 1 }}</td>
                            <td>{{ result.CREATED_AT | date: 'dd MMM yyyy, HH:mm' }}</td>
                            <td>{{ result.KATEGORI }}</td>
                            <td>{{ result.JUDUL }}</td>
                            <td *ngIf="activeTab === 'peserta'">{{ result.CREATED_BY }}</td>
                            <td *ngIf="activeTab === 'tutor'">{{ result.MAHASISWA }}</td>
                            <td class="text-center">{{ result.BOBOT }}</td>
                            <td class="text-center font-bold" [ngClass]="{'text-green-600': result.SKOR >= 75, 'text-red-500': result.SKOR < 75}">
                                {{ result.SKOR }}%
                            </td>
                            <td>{{ result.UNIVERSITAS }}</td>
                            <td class="text-center" (click)="$event.stopPropagation()">
                                <div class="flex gap-2 justify-center">
                                    <p-button 
                                        icon="pi pi-eye" 
                                        [text]="true" 
                                        [raised]="true" 
                                        severity="info" 
                                        (onClick)="viewDetail(result)"
                                        pTooltip="Lihat Detail"
                                        tooltipPosition="top">
                                    </p-button>
                                    <p-button 
                                        icon="pi pi-file-pdf" 
                                        [text]="true" 
                                        [raised]="true" 
                                        severity="danger" 
                                        [disabled]="fetchingData" 
                                        (onClick)="onPrint(result)"
                                        pTooltip="Unduh PDF"
                                        tooltipPosition="top">
                                    </p-button>
                                </div>
                            </td>
                        </tr>
                    </ng-template>

                    <ng-template pTemplate="empty">
                        <tr>
                            <td colspan="9" class="text-center p-4">Tidak ada data riwayat untuk kategori ini.</td>
                        </tr>
                    </ng-template>
                </p-table>
            </div>
        </div>

        <!-- Detail Dialog -->
        <p-dialog 
            [(visible)]="showDetailDialog" 
            [modal]="true" 
            [style]="{ width: '90vw', maxWidth: '850px' }"
            [draggable]="false"
            [resizable]="false"
            header="Detail Hasil Evaluasi OSCE">
            
            <div *ngIf="selectedReport" class="space-y-4">
                <!-- Metadata Report -->
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-100 mb-4">
                    <div>
                        <div class="text-xs text-gray-500">Mahasiswa</div>
                        <div class="font-bold text-gray-800">{{ selectedReport.MAHASISWA }}</div>
                    </div>
                    <div>
                        <div class="text-xs text-gray-500">Universitas</div>
                        <div class="font-bold text-gray-800">{{ selectedReport.UNIVERSITAS }}</div>
                    </div>
                    <div>
                        <div class="text-xs text-gray-500">Judul OSCE</div>
                        <div class="font-bold text-gray-800">{{ selectedReport.JUDUL }}</div>
                    </div>
                    <div>
                        <div class="text-xs text-gray-500">Skor Akhir</div>
                        <div class="font-bold text-lg text-primary">{{ selectedReport.SKOR }}% (Bobot: {{ selectedReport.BOBOT }})</div>
                    </div>
                </div>

                <p-progressbar *ngIf="detailsLoading" mode="indeterminate" [style]="{ height: '4px' }" />

                <!-- Checklist Table -->
                <div class="max-h-[50vh] overflow-y-auto" *ngIf="!detailsLoading && details.length > 0">
                    <p-table [value]="details" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header">
                            <tr>
                                <th class="w-12 text-center">No</th>
                                <th>Kegiatan</th>
                                <th colspan="4" class="text-center">Skor</th>
                                <th class="w-48">Komentar</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-item let-i="rowIndex">
                            <tr *ngIf="item.IS_HEADER == '1'" class="bg-blue-50 font-bold">
                                <td colspan="7" class="text-center !bg-primary text-white" [innerHTML]="sanitizeHtml(item.ACTIVITY)"></td>
                            </tr>
                            <tr *ngIf="item.IS_HEADER == '0'">
                                <td class="text-center">{{ getActivityNumber(i) }}</td>
                                <td [innerHTML]="sanitizeHtml(item.ACTIVITY)"></td>
                                <td class="text-center p-0">
                                    <div class="score-box" [ngClass]="{'selected': item.BOBOT == '0'}">0</div>
                                </td>
                                <td class="text-center p-0">
                                    <div class="score-box" [ngClass]="{'selected': item.BOBOT == '1'}">1</div>
                                </td>
                                <td class="text-center p-0">
                                    <div class="score-box" [ngClass]="{'selected': item.BOBOT == '2'}">2</div>
                                </td>
                                <td class="text-center p-0">
                                    <div class="score-box" [ngClass]="{'selected': item.BOBOT == '3'}">3</div>
                                </td>
                                <td>
                                    <span class="text-sm text-gray-600 block italic">{{ item.KOMENTAR || '-' }}</span>
                                </td>
                            </tr>
                        </ng-template>
                    </p-table>
                </div>
            </div>
            
            <ng-template pTemplate="footer">
                <div class="flex gap-2 justify-end">
                    <p-button 
                        label="Cetak PDF" 
                        icon="pi pi-file-pdf" 
                        severity="danger" 
                        [disabled]="fetchingData"
                        (onClick)="onPrint(selectedReport)">
                    </p-button>
                    <p-button 
                        label="Tutup" 
                        icon="pi pi-times" 
                        class="p-button-text" 
                        (onClick)="showDetailDialog = false">
                    </p-button>
                </div>
            </ng-template>
        </p-dialog>
        
        <style>
            .score-box {
                width: 40px;
                height: 40px;
                background-color: #0000001A;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                font-size: 16px;
                color: #00000033;
                border-radius: 4px;
            }
            
            .score-box.selected {
                background-color: #0D8A4533;
                color: #0D8A45;
            }
        </style>
    `
})
export class ParticipantChecklistHistory implements OnInit {
    participants: any[] = [];
    selectedParticipant: any = null;
    allReports: any[] = [];
    results: any[] = [];
    loading: boolean = false;
    fetchingData: boolean = false;
    detailsLoading: boolean = false;
    showDetailDialog: boolean = false;
    selectedReport: any = null;
    details: any[] = [];
    activeTab: string = 'peserta'; // 'peserta' or 'tutor'

    constructor(
        private userService: UserService,
        private messageService: MessageService,
        private sanitizer: DomSanitizer
    ) { }

    ngOnInit(): void {
        this.fetchInitialData();
    }

    fetchInitialData(): void {
        this.loading = true;
        this.fetchingData = true;

        // Fetch all users to get participant list
        this.userService.getUsers().then((response) => {
            const allUsers = response.data || [];
            // Role ID for Peserta is HAGSJ76H98
            this.participants = allUsers.filter((u: any) => u.ID_ACCESS_ROLE === 'HAGSJ76H98');
            this.loading = false;
        }).catch((error) => {
            console.error('Failed to fetch users:', error);
            this.loading = false;
            this.messageService.add({ severity: 'error', summary: 'Gagal', detail: 'Gagal memuat daftar peserta' });
        });

        // Fetch all OSCE reports
        this.userService.getOsceResult().then((response) => {
            this.allReports = response.data || [];
            this.fetchingData = false;
            if (this.selectedParticipant) {
                this.filterReports();
            }
        }).catch((error) => {
            console.error('Failed to fetch reports:', error);
            this.fetchingData = false;
            this.messageService.add({ severity: 'error', summary: 'Gagal', detail: 'Gagal memuat data laporan OSCE' });
        });
    }

    onParticipantChange(): void {
        this.filterReports();
    }

    setTab(tab: string): void {
        this.activeTab = tab;
        this.filterReports();
    }

    filterReports(): void {
        if (!this.selectedParticipant) {
            this.results = [];
            return;
        }

        const selectedName = this.selectedParticipant.FULLNAME.toLowerCase().trim();
        const selectedUsername = this.selectedParticipant.USERNAME.toLowerCase().trim();

        if (this.activeTab === 'peserta') {
            // Filter where the selected user is the student/mahasiswa evaluated
            this.results = this.allReports.filter((report: any) => {
                if (!report.MAHASISWA) return false;
                const reportMahasiswa = report.MAHASISWA.toLowerCase().trim();
                return reportMahasiswa === selectedName || reportMahasiswa === selectedUsername;
            });
        } else {
            // Filter where the selected user is the evaluator/tutor
            this.results = this.allReports.filter((report: any) => {
                if (!report.CREATED_BY) return false;
                const reportCreatedBy = report.CREATED_BY.toLowerCase().trim();
                return reportCreatedBy === selectedName || reportCreatedBy === selectedUsername;
            });
        }
    }

    onInput(event: Event, dt: any) {
        const inputElement = event.target as HTMLInputElement;
        dt.filterGlobal(inputElement.value, 'contains');
    }

    viewDetail(report: any): void {
        this.selectedReport = report;
        this.showDetailDialog = true;
        this.detailsLoading = true;
        this.details = [];

        this.userService.detailOsceResult({ ID_REPORT: report.id || report.ID_REPORT }).then((response) => {
            this.details = response.data || [];
            this.detailsLoading = false;
        }).catch((error) => {
            console.error('Failed to load report details:', error);
            this.detailsLoading = false;
            this.messageService.add({ severity: 'error', summary: 'Gagal', detail: 'Gagal memuat detail pengerjaan' });
        });
    }

    sanitizeHtml(html: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    getActivityNumber(index: number): number {
        let activityCount = 0;
        for (let i = 0; i <= index; i++) {
            if (this.details[i] && this.details[i].IS_HEADER === '0') {
                activityCount++;
            }
        }
        return activityCount;
    }

    onPrint(result: any): void {
        this.fetchingData = true;
        const reportId = result.id || result.ID_REPORT;

        this.userService.detailOsceResult({ ID_REPORT: reportId }).then((response) => {
            const detail = response.data || [];

            const headersAndActivities: any[] = [];
            let activityNumber = 0;
            detail.forEach((item: any) => {
                if (item.IS_HEADER === '1') {
                    headersAndActivities.push({ type: 'header', html: item.ACTIVITY });
                } else {
                    activityNumber++;
                    headersAndActivities.push({
                        type: 'activity',
                        number: activityNumber,
                        html: item.ACTIVITY,
                        skor: item.BOBOT,
                        komentar: item.KOMENTAR || ''
                    });
                }
            });

            function htmlToPdfMakeOps(html: string): any {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                function parseNode(node: any): any {
                    if (!node) return '';
                    if (node.nodeType === 3) return node.textContent;
                    if (node.nodeType !== 1) return '';
                    const tag = node.tagName.toLowerCase();
                    if (tag === 'b' || tag === 'strong') {
                        return { text: node.textContent, bold: true };
                    }
                    if (tag === 'ul' || tag === 'ol') {
                        const items = Array.from(node.children).filter((n: any) => n.tagName && n.tagName.toLowerCase() === 'li');
                        return {
                            [tag === 'ul' ? 'ul' : 'ol']: items.map((li: any) => parseNode(li))
                        };
                    }
                    if (tag === 'li') {
                        return Array.from(node.childNodes).map(parseNode);
                    }
                    if (tag === 'p') {
                        return { text: node.textContent, margin: [0, 2, 0, 2] };
                    }
                    return Array.from(node.childNodes).map(parseNode);
                }
                const body = doc.body;
                const children = Array.from(body.childNodes).map(parseNode);
                function flatten(arr: any): any {
                    return Array.isArray(arr) ? arr.reduce((a, b) => a.concat(flatten(b)), []) : [arr];
                }
                return flatten(children).filter((x: any) => x && (typeof x === 'string' || typeof x === 'object'));
            }

            const content: any[] = [
                { text: 'Laporan Hasil OSCE', style: 'header' },
                { text: `ID Laporan: ${reportId}`, margin: [0, 10, 0, 0] },
                { text: `Kategori: ${result.KATEGORI}` },
                { text: `Judul: ${result.JUDUL}` },
                { text: `Nama Mahasiswa: ${result.MAHASISWA}` },
                { text: `Universitas: ${result.UNIVERSITAS}` },
                { text: `Tutor/Penguji: ${result.CREATED_BY}` },
                { text: `Tanggal: ${result.CREATED_AT}`, margin: [0, 0, 0, 10] },
            ];

            content.push({
                table: {
                    widths: [30, '*', 30, 30, 30, 30, '*'],
                    body: [
                        [
                            { text: 'No', alignment: 'center', bold: true },
                            { text: 'KEGIATAN', bold: true },
                            { text: '0', alignment: 'center', bold: true },
                            { text: '1', alignment: 'center', bold: true },
                            { text: '2', alignment: 'center', bold: true },
                            { text: '3', alignment: 'center', bold: true },
                            { text: 'KOMENTAR', bold: true }
                        ],
                        ...headersAndActivities.map(item => {
                            if (item.type === 'header') {
                                return [
                                    { stack: htmlToPdfMakeOps(item.html), colSpan: 7, bold: true, fillColor: '#f0f0f0' }, '', '', '', '', '', ''
                                ];
                            } else {
                                const checkmark = '√';
                                return [
                                    { text: item.number.toString(), alignment: 'center' },
                                    { stack: htmlToPdfMakeOps(item.html) },
                                    { text: String(item.skor) === '0' ? checkmark : '', alignment: 'center' },
                                    { text: String(item.skor) === '1' ? checkmark : '', alignment: 'center' },
                                    { text: String(item.skor) === '2' ? checkmark : '', alignment: 'center' },
                                    { text: String(item.skor) === '3' ? checkmark : '', alignment: 'center' },
                                    item.komentar
                                ];
                            }
                        })
                    ]
                },
                layout: 'grid',
                margin: [0, 10, 0, 0]
            });

            const documentDefinition: TDocumentDefinitions = {
                pageOrientation: 'landscape',
                pageSize: 'A4',
                content,
                styles: {
                    header: {
                        fontSize: 18,
                        bold: true,
                        margin: [0, 0, 0, 10]
                    }
                },
                defaultStyle: {
                    fontSize: 9
                }
            };

            const filename = `${result.MAHASISWA}-${result.JUDUL}-${result.CREATED_AT}`.replace(/[\s:]/g, '_') + '.pdf';
            pdfMake.createPdf(documentDefinition).download(filename);
        }).catch((err) => {
            console.error('Failed to generate PDF:', err);
            this.messageService.add({ severity: 'error', summary: 'Gagal', detail: 'Gagal membuat PDF.' });
        }).finally(() => {
            this.fetchingData = false;
        });
    }
}
